import React from "react";
import { LegalLayout, Section } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <LegalLayout title="Terms of Service" updated="2026-05-17">
        <p className="text-zinc-300 leading-relaxed text-[15px]">
          These Terms of Service ("Terms") govern your access to and use of the CloudRent
          cloud hosting platform (the "Service") operated by CloudRent Hosting Ltd.
          ("we", "us", "our"). By creating an account or purchasing a server plan, you
          agree to be bound by these Terms. If you do not agree, do not use the Service.
        </p>

        <Section n={1} title="Accounts">
          <p>
            You must provide accurate and complete information when creating an account.
            You are responsible for all activity that occurs under your account and for
            keeping your credentials confidential. You must be at least 18 years old, or
            the age of majority in your jurisdiction, to create an account.
          </p>
        </Section>

        <Section n={2} title="The Service">
          <p>
            CloudRent provides cloud server hosting: virtual private servers (VPS) and
            dedicated bare-metal servers, together with a web dashboard to manage them.
            Each plan defines its CPU, memory, storage, billing period, and included
            features on the relevant product page. Plans are billed in advance on a
            recurring monthly basis until cancelled.
          </p>
          <p>
            We may suspend or terminate services that violate our acceptable-use rules,
            including but not limited to: hosting malware, phishing, child sexual abuse
            material, sustained denial-of-service attacks, or unsolicited bulk email.
          </p>
        </Section>

        <Section n={3} title="Payments">
          <p>
            By purchasing a plan, you authorize us (or our designated third-party
            checkout provider) to charge your selected payment method for the amount
            displayed at checkout, including any applicable taxes. Card details are
            collected on our checkout provider's certified environment — we never
            see or store your raw card number.
          </p>
        </Section>

        <Section n={4} title="Refunds & Cancellations">
          <p>
            You may cancel a recurring server plan from your dashboard at any time.
            Cancellations take effect at the end of the current billing period. Refund
            eligibility is described in our <a className="text-signal hover:underline" href="/refund">Refund Policy</a>.
          </p>
        </Section>

        <Section n={5} title="Acceptable Use">
          <p>You agree not to use a CloudRent server to:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>Violate any applicable law or regulation.</li>
            <li>Distribute malware, spyware, or other malicious code.</li>
            <li>Attack, probe, or scan any network you do not own.</li>
            <li>Send unsolicited bulk communications ("spam").</li>
            <li>Host content that exploits minors.</li>
            <li>Resell raw bandwidth or compute to anonymized third parties.</li>
          </ul>
        </Section>

        <Section n={6} title="Service Availability & SLAs">
          <p>
            We target 99.95% uptime on paid plans. Where a published SLA exists for a
            specific service, eligible downtime credits will be applied on request as
            described on that service's documentation page. We do not warrant
            uninterrupted or error-free operation.
          </p>
        </Section>

        <Section n={7} title="Your Data">
          <p>
            You retain ownership of all data you store on a CloudRent server. We take
            reasonable measures (encrypted backups, access controls) to protect your
            data, but you are responsible for maintaining your own off-site backups of
            anything you cannot afford to lose.
          </p>
        </Section>

        <Section n={8} title="Intellectual Property">
          <p>
            All trademarks, logos, and software displayed on the Service remain the
            property of CloudRent Hosting Ltd. or its licensors. Limited license is
            granted to you to use the Service in accordance with these Terms.
          </p>
        </Section>

        <Section n={9} title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, CloudRent's aggregate liability
            arising out of or relating to the Service shall not exceed the amount you
            paid us in the twelve (12) months preceding the event giving rise to the
            claim. We are not liable for indirect, incidental, consequential, or
            punitive damages.
          </p>
        </Section>

        <Section n={10} title="Termination">
          <p>
            We may suspend or terminate your access at any time, with or without notice,
            for conduct that we believe violates these Terms or harms other users.
          </p>
        </Section>

        <Section n={11} title="Governing Law & Disputes">
          <p>
            These Terms are governed by the laws of England and Wales. Disputes shall be
            resolved exclusively in the courts of London, United Kingdom, except where
            applicable consumer-protection law grants you a non-waivable forum.
          </p>
        </Section>

        <Section n={12} title="Contact">
          <p>
            Questions about these Terms? Email <a className="text-signal hover:underline" href="mailto:legal@cloudrentvps.com">legal@cloudrentvps.com</a>.
          </p>
        </Section>
      </LegalLayout>
    </div>
  );
}
